import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Package, Grid, ShoppingBag, Users, BarChart3, FileText, Plus, Trash2, Pencil } from "lucide-react";

const emptyProduct = { name: "", slug: "", description: "", price: 0, sale_price: null, category_slug: "", sku: "", in_stock: true, featured: false, best_seller: false, new_arrival: false, tags: [], images: [""], gallery: [], download_files: [], download_url: "", related_products: [] };

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("products");
  if (loading) return <div className="container-luxe py-32">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" />;

  const tabs = [
    { k: "products", t: "Products", i: Package },
    { k: "categories", t: "Categories", i: Grid },
    { k: "orders", t: "Orders", i: ShoppingBag },
    { k: "customers", t: "Customers", i: Users },
    { k: "analytics", t: "Analytics", i: BarChart3 },
    { k: "cms", t: "CMS", i: FileText },
  ];

  return (
    <div className="container-luxe py-16">
      <div className="mb-10">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Admin</div>
        <h1 className="font-display text-4xl font-semibold">PLAYA Console</h1>
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="glass rounded-2xl p-3 h-fit">
          {tabs.map((t) => (
            <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)} className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 text-sm transition ${tab === t.k ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-gray-300 hover:text-white"}`}>
              <t.i size={16} /> {t.t}
            </button>
          ))}
        </aside>
        <div className="glass rounded-2xl p-8">
          {tab === "products" && <ProductsAdmin />}
          {tab === "categories" && <CategoriesAdmin />}
          {tab === "orders" && <OrdersAdmin />}
          {tab === "customers" && <CustomersAdmin />}
          {tab === "analytics" && <AnalyticsAdmin />}
          {tab === "cms" && <CMSAdmin />}
        </div>
      </div>
    </div>
  );
}

function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/categories").then((r) => setCats(r.data)); }, []);

  const start = (p) => { setEditing(p); setForm(p ? { ...emptyProduct, ...p } : emptyProduct); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), sale_price: form.sale_price ? parseFloat(form.sale_price) : null };
      if (editing?.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/products/${id}`); load(); };

  const uploadFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("playa_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/product-file`, {
        method: "POST", credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const meta = await res.json();
      setForm({ ...form, download_files: [...(form.download_files || []), meta] });
      toast.success("File uploaded");
    } catch (err) { toast.error(err.message); }
  };

  const uploadMedia = async (e, kind) => {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;
    const token = localStorage.getItem("playa_token");
    const backend = process.env.REACT_APP_BACKEND_URL;
    const uploaded = [];
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const res = await fetch(`${backend}/api/upload/media`, {
          method: "POST", credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
        });
        if (!res.ok) { toast.error(`Upload failed: ${file.name}`); continue; }
        const meta = await res.json();
        uploaded.push(`${backend}${meta.url}`);
      } catch { toast.error(`Upload failed: ${file.name}`); }
    }
    if (uploaded.length === 0) return;
    if (kind === "main") setForm({ ...form, images: uploaded.slice(0, 1) });
    else setForm({ ...form, gallery: [...(form.gallery || []), ...uploaded] });
    toast.success(`${uploaded.length} image(s) uploaded`);
  };

  return (
    <>
      <div className="flex justify-between mb-6">
        <h2 className="font-display text-2xl">Products ({items.length})</h2>
        <button data-testid="admin-new-product" onClick={() => start(null)} className="btn-gold text-sm"><Plus size={14} /> New</button>
      </div>
      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.slug}`} className="flex justify-between items-center py-3 border-b border-white/5">
            <div>
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-gray-500">${p.price} · {p.sku} · {p.category_slug}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => start(p)} className="text-gray-400 hover:text-[#D4AF37]"><Pencil size={16} /></button>
              <button onClick={() => del(p.id)} className="text-gray-400 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="glass rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-xl mb-6">{editing ? "Edit" : "New"} Product</h3>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")})} />
              <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} />
              <input placeholder="SKU" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} />
              <select value={form.category_slug} onChange={(e) => setForm({...form, category_slug: e.target.value})}>
                <option value="">Category</option>
                {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} />
              <input type="number" step="0.01" placeholder="Sale Price (optional)" value={form.sale_price || ""} onChange={(e) => setForm({...form, sale_price: e.target.value || null})} />
              <input className="col-span-2" placeholder="Image URL (or upload below)" value={form.images?.[0] || ""} onChange={(e) => setForm({...form, images: [e.target.value]})} />
              <div className="col-span-2 grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Main image upload</label>
                  <input data-testid="admin-upload-main" type="file" accept="image/*" onChange={(e) => uploadMedia(e, "main")} />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Gallery upload (multi)</label>
                  <input data-testid="admin-upload-gallery" type="file" accept="image/*" multiple onChange={(e) => uploadMedia(e, "gallery")} />
                </div>
              </div>
              {form.gallery?.length > 0 && (
                <div className="col-span-2 flex flex-wrap gap-2">
                  {form.gallery.map((g, i) => (
                    <div key={i} className="relative group">
                      <img src={g} alt="" className="w-16 h-16 object-cover rounded" />
                      <button type="button" onClick={() => setForm({...form, gallery: form.gallery.filter((_, idx) => idx !== i)})} className="absolute -top-1 -right-1 bg-black/80 border border-white/20 rounded-full w-5 h-5 text-xs opacity-0 group-hover:opacity-100">×</button>
                    </div>
                  ))}
                </div>
              )}
              <textarea className="col-span-2" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-auto" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-auto" checked={form.best_seller} onChange={(e) => setForm({...form, best_seller: e.target.checked})} /> Best Seller</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-auto" checked={form.new_arrival} onChange={(e) => setForm({...form, new_arrival: e.target.checked})} /> New Arrival</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="w-4 h-auto" checked={form.in_stock} onChange={(e) => setForm({...form, in_stock: e.target.checked})} /> In Stock</label>
              <div className="col-span-2">
                <label className="text-sm text-gray-400 block mb-2">Digital Product Delivery</label>
                <input className="mb-2" placeholder="External URL (Google Drive, Dropbox, Notion, etc.) — optional" value={form.download_url || ""} onChange={(e) => setForm({...form, download_url: e.target.value})} />
                <div className="text-xs text-gray-500 mb-3">— or upload a file below —</div>
                <input type="file" onChange={uploadFile} />
                {form.download_files?.length > 0 && <div className="text-xs text-gray-400 mt-2">{form.download_files.length} file(s) attached</div>}
                {form.download_url && <div className="text-xs text-[#00A86B] mt-2">✓ URL will be delivered to customer</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              <button data-testid="admin-save-product" onClick={save} className="btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/categories", form); toast.success("Created"); setForm({ name: "", slug: "", description: "" }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/categories/${id}`); load(); };
  return (
    <>
      <h2 className="font-display text-2xl mb-6">Categories</h2>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")})} />
        <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} />
        <button onClick={save} className="btn-gold">Add</button>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex justify-between py-3 border-b border-white/5">
            <div><div>{c.name}</div><div className="text-xs text-gray-500">{c.slug}</div></div>
            <button onClick={() => del(c.id)} className="text-gray-400 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function OrdersAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/orders${q ? `?q=${q}` : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q]);
  const refund = async (id) => { await api.post(`/admin/orders/${id}/refund`); load(); };
  const complete = async (id) => { await api.post(`/admin/orders/${id}/complete`); load(); };
  return (
    <>
      <h2 className="font-display text-2xl mb-6">Orders</h2>
      <input placeholder="Search by order ID or email" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 max-w-md" />
      <div className="space-y-2">
        {items.map((o) => (
          <div key={o.id} className="glass rounded-lg p-4 flex justify-between items-center">
            <div>
              <div className="text-sm">#{o.id.slice(0, 8)} · {o.user_email || "guest"}</div>
              <div className="text-xs text-gray-500">${o.total?.toFixed(2)} · {o.status}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => complete(o.id)} className="btn-ghost text-xs">Complete</button>
              <button onClick={() => refund(o.id)} className="btn-ghost text-xs">Refund</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function CustomersAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data)); }, []);
  return (
    <>
      <h2 className="font-display text-2xl mb-6">Customers</h2>
      <div className="space-y-2">
        {items.map((u) => (
          <div key={u.user_id} className="flex justify-between py-3 border-b border-white/5">
            <div><div>{u.name}</div><div className="text-xs text-gray-500">{u.email} · {u.role}</div></div>
            <div className="text-sm text-gray-400">{u.order_count} orders</div>
          </div>
        ))}
      </div>
    </>
  );
}

function AnalyticsAdmin() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return <div>Loading…</div>;
  const cards = [
    { t: "Total Revenue", v: `$${a.revenue}` },
    { t: "Daily", v: `$${a.daily}` },
    { t: "Weekly", v: `$${a.weekly}` },
    { t: "Monthly", v: `$${a.monthly}` },
    { t: "Customers", v: a.customers },
    { t: "Orders", v: a.orders_count },
    { t: "Conversion", v: `${a.conversion_rate}%` },
  ];
  return (
    <>
      <h2 className="font-display text-2xl mb-6">Analytics</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.t} className="glass rounded-xl p-5">
            <div className="text-xs tracking-widest uppercase text-gray-500">{c.t}</div>
            <div className="font-display text-2xl text-[#D4AF37] mt-2">{c.v}</div>
          </div>
        ))}
      </div>
      <h3 className="font-display text-lg mb-3">Best Selling</h3>
      <div className="space-y-2">
        {a.best_selling.map((p) => (
          <div key={p.name} className="flex justify-between py-2 border-b border-white/5 text-sm">
            <span>{p.name}</span><span className="text-[#00A86B]">{p.quantity} sold</span>
          </div>
        ))}
      </div>
    </>
  );
}

function CMSAdmin() {
  const [pages, setPages] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get("/cms").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);
  const edit = (p) => { setSel(p); setForm({ title: p.title, body: p.body }); };
  const save = async () => { await api.put(`/cms/${sel.slug}`, { slug: sel.slug, ...form }); toast.success("Saved"); load(); };
  return (
    <>
      <h2 className="font-display text-2xl mb-6">CMS Pages</h2>
      <div className="grid grid-cols-[200px_1fr] gap-6">
        <div className="space-y-1">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => edit(p)} className={`w-full text-left px-3 py-2 rounded text-sm ${sel?.slug === p.slug ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-gray-300 hover:text-white"}`}>{p.title}</button>
          ))}
        </div>
        {sel && (
          <div className="space-y-3">
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
            <textarea rows={12} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} />
            <button onClick={save} className="btn-gold">Save</button>
          </div>
        )}
      </div>
    </>
  );
}
